/**
 * EdgeHealthView — Pilar 2.5 estabilidad.
 *
 * Semáforo del estado de cada M4 según el snapshot que el backend recolecta
 * cada 60s de `/health/deep`. Muestra:
 *   - Estado overall por sitio (up / down / unknown) con texto explícito
 *     (Paulo es daltónico — color es ayuda visual, NO la única señal)
 *   - Drill-down por componente: caddy, sidecar, mediamtx, daemon
 *   - Recursos del M4: cpu, ram, disco, load
 *   - Paths MediaMTX listos vs total (semáforo por path)
 *   - "Última actualización" para detectar staleness del poller
 *
 * Endpoint: GET /api/edge/health (snapshot agregado) y
 *           GET /api/edge/health/{site_id} (detalle al click)
 */
import { useEffect, useState, useCallback, useMemo } from 'react'
import { useAuthStore } from '../store/authStore'

// i18n compact — cad.retaliq.net (cement demo) muestra todo en inglés.
const _isEN = typeof window !== 'undefined' && window.location.hostname === 'cad.retaliq.net'
const T = _isEN ? {
  loading:    'Loading edge nodes status…',
  pageTitle:  'RIQ Edge Health',
  pageSubtitle: 'Live status of every deployed RIQ edge appliance. Backend polls every 60s. UI refreshes every 30s.',
  lastRead:   'Last UI read',
  errorPrefix:'Backend query error',
  operational:'Operational',
  down:       'Down',
  unknown:    'Unknown',
  activeAlerts:'Active alerts',
  refresh:    'Refresh',
  siteId:     'site_id',
  lastRead2:  'last read',
  ago:        'ago',
  resources:  'Edge resources',
  cpuNormal:  'normal', cpuWarn: 'high', cpuCrit: 'critical',
  mediaPathsLabel: 'MediaMTX paths & raw payload',
  never:      'never',
  UP:  { label: 'OPERATIONAL', tag: 'UP' },
  DOWN:{ label: 'DOWN',        tag: 'DOWN' },
  UNK: { label: 'UNKNOWN',     tag: 'UNK' },
} : {
  loading:    'Cargando estado de los M4…',
  pageTitle:  'Salud de Edge / M4',
  pageSubtitle: 'Estado en vivo de cada Mac mini desplegado. Backend consulta cada 60s. UI refresca cada 30s.',
  lastRead:   'Última lectura UI',
  errorPrefix:'Error consultando backend',
  operational:'Operativos',
  down:       'Caídos',
  unknown:    'Desconocidos',
  activeAlerts:'Alertas activas',
  refresh:    'Refrescar',
  siteId:     'site_id',
  lastRead2:  'última lectura',
  ago:        '',
  resources:  'Recursos del M4',
  cpuNormal:  'normal', cpuWarn: 'alta', cpuCrit: 'crítica',
  mediaPathsLabel: 'Ver paths MediaMTX y payload crudo',
  never:      'nunca',
  UP:  { label: 'OPERATIVO',   tag: 'UP' },
  DOWN:{ label: 'CAÍDO',       tag: 'DOWN' },
  UNK: { label: 'DESCONOCIDO', tag: 'UNK' },
}

// ─── Estado → label + color textual ──────────────────────────────────────
// Importante: el color es VISUAL HINT. El status va SIEMPRE en texto.
const STATUS_META = {
  up      : { label: T.UP.label,   dot: '#21a366', tag: T.UP.tag },
  down    : { label: T.DOWN.label, dot: '#d93025', tag: T.DOWN.tag },
  unknown : { label: T.UNK.label,  dot: '#8a8a8a', tag: T.UNK.tag },
}

function statusMeta(s) {
  return STATUS_META[s] || STATUS_META.unknown
}


// ─── Cómo se sintetiza un site_id a nombre legible ────────────────────────
const SITE_NAMES = _isEN ? {
  21: 'RIQ-1 · Ambuja Rabriyawas',
  22: 'RIQ-2 · UltraTech Rajashree Malkhed',
  23: 'RIQ-3 · Shree Cement Beawar',
} : {
  17: 'Latadia EDS Copec',
  19: 'Hites Alameda',
  20: 'Cat System (M4)',
  21: 'RIQ-1 · Ambuja Rabriyawas',
}


function StatusPill({ status }) {
  const m = statusMeta(status)
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
      background: 'rgba(255,255,255,0.04)', border: `1px solid ${m.dot}`,
      color: m.dot, letterSpacing: 0.3,
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%', background: m.dot,
      }} />
      [{m.tag}] {m.label}
    </span>
  )
}


function ResourceBar({ label, value, unit = '%', warn = 80, crit = 95 }) {
  if (value === null || value === undefined) {
    return (
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        {label}: <span style={{ fontWeight: 600 }}>—</span> ({_isEN ? label + ' unavailable' : label + ' no disponible'})
      </div>
    )
  }
  const v = Number(value)
  let band = 'normal'
  let bandColor = '#21a366'
  let bandLabel = 'normal'
  if (v >= crit) { band = 'crit'; bandColor = '#d93025'; bandLabel = _isEN ? 'CRITICAL' : 'CRÍTICO' }
  else if (v >= warn) { band = 'warn'; bandColor = '#e8a704'; bandLabel = _isEN ? 'HIGH' : 'ALTO' }
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 }}>
        <span style={{ color: 'var(--text-muted)' }}>{label}</span>
        <span style={{ fontWeight: 600, color: bandColor }}>
          {v.toFixed(1)}{unit} — {bandLabel}
        </span>
      </div>
      <div style={{
        background: 'rgba(255,255,255,0.06)', borderRadius: 4, height: 6, overflow: 'hidden',
      }}>
        <div style={{
          width: `${Math.min(100, v)}%`, height: '100%', background: bandColor,
          transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  )
}


function ComponentRow({ name, comp }) {
  // comp shape: { status, paths_ready?, paths_total?, pending_events? }
  const status = (comp && comp.status) || 'unknown'
  const m = statusMeta(status)
  const extras = []
  if (comp?.paths_ready !== undefined) {
    const ready = comp.paths_ready
    const total = comp.paths_total
    extras.push(`${ready}/${total} ${_isEN ? 'paths ready' : 'paths listos'}`)
  }
  if (comp?.pending_events !== undefined && comp.pending_events !== null) {
    extras.push(`${comp.pending_events} ${_isEN ? 'pending events' : 'eventos pendientes'}`)
  }
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '8px 12px', borderRadius: 6, marginBottom: 4,
      background: 'rgba(255,255,255,0.02)',
      border: `1px solid ${m.dot}33`,
    }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
          {name}
        </div>
        {extras.length > 0 && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            {extras.join(' · ')}
          </div>
        )}
      </div>
      <StatusPill status={status} />
    </div>
  )
}


function MediaMtxPaths({ paths }) {
  if (!paths || paths.length === 0) return null
  const ready = paths.filter(p => p.ready).length
  return (
    <details style={{ marginTop: 12 }}>
      <summary style={{
        cursor: 'pointer', fontSize: 12, fontWeight: 500, color: 'var(--text-muted)',
      }}>
        {_isEN ? 'MediaMTX paths' : 'Paths MediaMTX'} ({ready}/{paths.length} {_isEN ? 'ready' : 'listos'}) — {_isEN ? 'click for detail' : 'click para ver detalle'}
      </summary>
      <div style={{ marginTop: 8, fontSize: 11 }}>
        {paths.map(p => (
          <div key={p.name} style={{
            display: 'flex', justifyContent: 'space-between',
            padding: '4px 8px', borderRadius: 3,
            background: p.ready ? 'rgba(33,164,102,0.08)' : 'rgba(217,48,37,0.08)',
            marginBottom: 2,
          }}>
            <span style={{ fontFamily: 'monospace' }}>{p.name}</span>
            <span style={{
              color: p.ready ? '#21a366' : '#d93025', fontWeight: 600,
            }}>
              {p.ready ? (_isEN ? '[READY]' : '[LISTO]') : (_isEN ? '[NOT READY]' : '[NO LISTO]')} · {(p.bytes_received / 1e6).toFixed(1)} MB
            </span>
          </div>
        ))}
      </div>
    </details>
  )
}


function SiteCard({ siteId, snap, detail, onExpand, expanded }) {
  const m = statusMeta(snap?.overall)
  const updated = snap?.updated_at ? new Date(snap.updated_at) : null
  const ageS = updated ? Math.floor((Date.now() - updated.getTime()) / 1000) : null
  const stale = ageS !== null && ageS > 180  // >3 min sin update = poller atascado

  return (
    <div style={{
      background: 'var(--bg-elev-1)',
      border: `1px solid ${m.dot}66`,
      borderRadius: 8, padding: 16, marginBottom: 12,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 12,
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
            {SITE_NAMES[siteId] || `Sitio ${siteId}`}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
            {T.siteId}={siteId}
            {ageS !== null && (
              <> · {T.lastRead2}: {_isEN ? `${ageS}s ago` : `hace ${ageS}s`} {stale && <strong style={{ color: '#e8a704' }}> ⚠ STALE</strong>}</>
            )}
            {snap?.alert_active && (
              <strong style={{ color: '#d93025' }}> · {_isEN ? 'ACTIVE ALERT' : 'ALERTA ACTIVA'}</strong>
            )}
          </div>
        </div>
        <StatusPill status={snap?.overall} />
      </div>

      {/* Componentes */}
      <div style={{ marginBottom: 12 }}>
        {snap?.components ? (
          Object.entries(snap.components).map(([k, v]) => (
            <ComponentRow key={k} name={k} comp={typeof v === 'string' ? { status: v } : v} />
          ))
        ) : (
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {_isEN ? 'No component data — backend poller has not queried this site yet.' : 'Sin datos de componentes — backend poller aún no consultó este sitio.'}
          </div>
        )}
      </div>

      {/* Recursos */}
      {snap?.resources && (
        <div style={{
          padding: 12, background: 'rgba(255,255,255,0.02)',
          borderRadius: 6, marginBottom: 8,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--text-muted)' }}>
            {T.resources}
          </div>
          <ResourceBar label="CPU"  value={snap.resources.cpu_pct}  warn={70} crit={90} />
          <ResourceBar label={_isEN ? 'RAM' : 'RAM'}  value={snap.resources.mem_pct || snap.resources.ram_pct}  warn={75} crit={90} />
          <ResourceBar label={_isEN ? 'DISK' : 'DISCO'} value={snap.resources.disk_pct} warn={80} crit={92} />
          {snap.resources.load_1m !== null && snap.resources.load_1m !== undefined && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Load 1min: <strong style={{ color: 'var(--text-primary)' }}>{snap.resources.load_1m}</strong>
            </div>
          )}
        </div>
      )}

      {/* Drill-down */}
      <button
        onClick={() => onExpand(siteId)}
        style={{
          width: '100%', padding: '8px 12px', border: '1px solid var(--border)',
          background: expanded ? 'rgba(255,255,255,0.04)' : 'transparent',
          color: 'var(--text-primary)', borderRadius: 6, cursor: 'pointer',
          fontSize: 12,
        }}
      >
        {expanded ? (_isEN ? '▼ Hide detail' : '▼ Ocultar detalle') : (_isEN ? '▶ Show MediaMTX paths & raw payload' : '▶ Ver paths MediaMTX y payload crudo')}
      </button>

      {expanded && detail && (
        <div style={{ marginTop: 12 }}>
          {detail.payload?.mediamtx_paths && (
            <MediaMtxPaths paths={detail.payload.mediamtx_paths} />
          )}
          <details style={{ marginTop: 12 }}>
            <summary style={{ cursor: 'pointer', fontSize: 11, color: 'var(--text-muted)' }}>
              JSON crudo del snapshot
            </summary>
            <pre style={{
              fontSize: 10, background: 'rgba(0,0,0,0.4)', padding: 8,
              borderRadius: 4, marginTop: 8, overflow: 'auto', maxHeight: 300,
            }}>
              {JSON.stringify(detail, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  )
}


export default function EdgeHealthView() {
  const token = useAuthStore(s => s.token)
  const [list, setList]       = useState({})
  const [details, setDetails] = useState({})   // {site_id: detail}
  const [loading, setLoading] = useState(true)
  const [err, setErr]         = useState(null)
  const [expanded, setExpanded] = useState(null)
  const [lastRefresh, setLastRefresh] = useState(null)

  const fetchList = useCallback(async () => {
    if (!token) return
    try {
      const r = await fetch('/api/edge/health', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const body = await r.json()
      setList(body)
      setLastRefresh(new Date())
      setErr(null)
    } catch (e) {
      setErr(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }, [token])

  // Polling cada 30s para mantener semáforo fresco
  useEffect(() => {
    fetchList()
    const id = setInterval(fetchList, 30000)
    return () => clearInterval(id)
  }, [fetchList])

  const onExpand = useCallback(async (siteId) => {
    if (expanded === siteId) {
      setExpanded(null); return
    }
    setExpanded(siteId)
    if (!details[siteId] && token) {
      try {
        const r = await fetch(`/api/edge/health/${siteId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (r.ok) {
          const d = await r.json()
          setDetails(prev => ({ ...prev, [siteId]: d }))
        }
      } catch (e) {
        console.error('detail fetch fail', e)
      }
    }
  }, [expanded, details, token])

  const sortedEntries = useMemo(() => {
    return Object.entries(list).sort(([a], [b]) => Number(a) - Number(b))
  }, [list])

  // Summary counts
  const counts = useMemo(() => {
    const c = { up: 0, down: 0, unknown: 0, alerts: 0 }
    Object.values(list).forEach(s => {
      c[s.overall] = (c[s.overall] || 0) + 1
      if (s.alert_active) c.alerts += 1
    })
    return c
  }, [list])

  if (loading) {
    return (
      <div style={{ padding: 24, color: 'var(--text-muted)' }}>
        {T.loading}
      </div>
    )
  }

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{
          fontSize: 22, fontWeight: 600, color: 'var(--text-primary)',
          margin: 0, marginBottom: 8,
        }}>
          {T.pageTitle}
        </h1>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {T.pageSubtitle}
          {lastRefresh && (
            <> {T.lastRead}: {lastRefresh.toLocaleTimeString()}</>
          )}
        </div>
        {err && (
          <div style={{
            marginTop: 8, padding: 8, fontSize: 12,
            background: 'rgba(217,48,37,0.1)', border: '1px solid #d93025',
            color: '#d93025', borderRadius: 4,
          }}>
            {T.errorPrefix}: {err}
          </div>
        )}
      </div>

      {/* Resumen agregado */}
      <div style={{
        display: 'flex', gap: 16, marginBottom: 24,
        padding: 16, background: 'var(--bg-elev-1)', borderRadius: 8,
        border: '1px solid var(--border)',
      }}>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{T.operational}</div>
          <div style={{ fontSize: 24, fontWeight: 600, color: '#21a366' }}>
            {counts.up}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{T.down}</div>
          <div style={{ fontSize: 24, fontWeight: 600, color: '#d93025' }}>
            {counts.down}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{T.unknown}</div>
          <div style={{ fontSize: 24, fontWeight: 600, color: '#8a8a8a' }}>
            {counts.unknown}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{T.activeAlerts}</div>
          <div style={{ fontSize: 24, fontWeight: 600, color: counts.alerts > 0 ? '#d93025' : 'var(--text-muted)' }}>
            {counts.alerts}
          </div>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <button
            onClick={fetchList}
            style={{
              padding: '8px 16px', border: '1px solid var(--border)',
              background: 'transparent', color: 'var(--text-primary)',
              borderRadius: 6, cursor: 'pointer', fontSize: 12,
            }}
          >
            {T.refresh}
          </button>
        </div>
      </div>

      {/* Sites */}
      {sortedEntries.length === 0 ? (
        <div style={{
          padding: 24, textAlign: 'center', color: 'var(--text-muted)',
          background: 'var(--bg-elev-1)', borderRadius: 8,
        }}>
          No hay snapshots aún. El poller corre cada 60s en el backend —
          espera ~1 min después de iniciar el sistema.
        </div>
      ) : (
        sortedEntries.map(([siteId, snap]) => (
          <SiteCard
            key={siteId}
            siteId={Number(siteId)}
            snap={snap}
            detail={details[siteId]}
            expanded={expanded === Number(siteId)}
            onExpand={onExpand}
          />
        ))
      )}
    </div>
  )
}
